import {
	AfterViewChecked,
	AfterViewInit,
	Component,
	Input,
	NgZone,
	OnInit,
	Output,
	ViewChild,
} from '@angular/core';
import { Subscription, take } from 'rxjs';
import { Pageable, PageableSort, PageableSortOrder } from '../../../../app/core/models/pageable';
import { MatPaginator } from '@angular/material/paginator';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { environment } from '../../../../environments/environment';
import { SelectionModel } from '@angular/cdk/collections';
import { MatSort } from '@angular/material/sort';
import { RestPagedResponse } from '../../../core/models/rest-page-response.model';
import { RoleBasedParamModel } from '../../../core/models/role-based-param.model';
import { ExcelFileService } from '../../../../app/core/services/excel-file.service';
import { CustomTableColumn } from './models/custom-table-column.model';
import { ThenableEventEmitter } from '../../utils/thenable-event';
import { DatePipe } from '@angular/common';

@Component({
	selector: 'ag-custom-table',
	templateUrl: './custom-table.component.html',
	styleUrls: ['./custom-table.component.scss'],
	standalone: false
})
export class CustomTableComponent implements OnInit, AfterViewChecked, AfterViewInit {
	@ViewChild(MatPaginator) paginator: MatPaginator;
	@ViewChild(MatSort) sort: MatSort;
	// Needed to help with sticky table column overlap
	@ViewChild('matTable', { static: false }) set table(matTable: MatTable<any>) {
		if (matTable) {
			this.ngZone.onMicrotaskEmpty
				.pipe(take(3))
				.subscribe(() => matTable.updateStickyColumnStyles());
		}
	}

	subscription = new Subscription();
	childSubscription: Subscription;

	firstLoad = true;
	sticky = false;

	public stickyColumns: string[] = [];
	public stickyEndColumns: string[] = [];
	/** Input used to provide an array of configurable columns to render the table */
	@Input()
	public columns: CustomTableColumn[] = [];
	/** Input used to provide a function that will be used to collect data for displaying in the table, PAGED.
	 * Also used in the export function. Users must pay attention to overwrite the default pageable size in Spring
	 */
	@Input()
	public getData: Function;
	/** Input for the data to be rendered in the table if you're not making an API call for it. Usecase: dialog */
	@Input()
	public data: any[];
	/** Input for the name for the excel file in case the Export feature is used. Default value: "DefaultExcelName" */
	@Input()
	public excelName: string = "DefaultExcelName";
	/** Input for the instance of the parent component */
	@Input()
	public instance: Component;
	/** Input for checking if a row is clickable */
	@Input()
	public clickableRow: Function;
	/** Input used to have access to the selected elements in parent component if needed */
	@Input()
	selection = new SelectionModel<any>(true, []);

	/** Output emitted on page/sort changes */
	@Output()
	public changeData = new ThenableEventEmitter<any>();
	/** Output emitted on export */
	@Output()
	public exportData = new ThenableEventEmitter<any>();
	/** Output emitted on row click */
	@Output()
	public clickedRow = new ThenableEventEmitter<any>();


	/** Table Variables */
	dataSource: MatTableDataSource<any> = new MatTableDataSource<any>(
		[]
	);
	pageOptions: number[] = environment.tablePaginationSizes;
	totalRows: number;
	searchResults = false;
	loadingRecords = false;

	isToggleSelectAll = false;
	exclusionSelectList = new Map<number, any>();

	public tooltipMsg: any = {
		mainFilterWasChanged: 'Tooltip message',
	};

	public displayedColumns: string[] = [];
	private defaultSort: PageableSort[] = [];

	constructor(
		private readonly excelService: ExcelFileService,
		private readonly ngZone: NgZone,
		private readonly datePipe: DatePipe,
	) {
	}

	ngOnInit(): void {
		this.stickyColumns = this.columns.filter(column => column.locked).map(column => column.id);
		this.stickyEndColumns = this.columns.filter(column => column.lockedEnd).map(column => column.id);
		this.displayedColumns = this.columns.map(column => column.id);
		this.defaultSort = this.columns.filter(column => column.defaultSort)
			.sort((a, b) => a.defaultSort - b.defaultSort)
			.map(column => { return { field: column.sortProperty, order: column.defaultSortOrder } as PageableSort });
		this.dataSource = new MatTableDataSource<any>([]);

		if (this.data !== undefined) {
			if (this.data.length > 0) {
				this.searchResults = true;
				this.dataSource.data = this.data;
			} else {
				this.searchResults = false;
			}
			this.totalRows = this.data.length;
			this.loadingRecords = false;
			return;
		}
		if (this.paginator) {
			this.getPagedData(this.paginator.pageIndex, this.paginator.pageSize);
		} else {
			this.getPagedData(0, this.pageOptions[0]);
		}
	}

	/**
	 * Required in order to maintain Sticky Columns
	 */
	ngAfterViewChecked(): void {
		if (this.table) {
			this.table.updateStickyColumnStyles();
		}
	}

	ngAfterViewInit(): void {
		if (this.data !== undefined) {
			if (this.data.length > 0) {
				this.dataSource.paginator = this.paginator;
				this.dataSource.sort = this.sort;
			}
		}
	}

	private getPageableSort() {
		if (!this.sort?.active) {
			return this.defaultSort;
		}

		const pageableSort: PageableSort[] = [];
		const order = this.sort.direction === 'asc'
			? PageableSortOrder.ASC
			: PageableSortOrder.DESC;
		pageableSort.push({
			field: this.sort.active,
			order
		});

		const index = this.defaultSort.findIndex(
			(header) => header.field === this.sort?.active
		);
		if (index !== -1) {
			this.defaultSort.forEach((header, headerIndex) => {
				if (headerIndex !== index) {
					pageableSort.push(header);
				}
			});
		} else {
			pageableSort.push(...this.defaultSort);
		}

		return pageableSort;
	}

	getPagedData(page: number, size: number) {
		this.searchResults = true;
		this.loadingRecords = true;
		this.changeData.emit({ pageable: new Pageable(page, size, this.getPageableSort()) }).then((response: any) => {
			const data: RestPagedResponse<any, any> = response;
			if ((data.totalElements !== undefined && data.totalElements > 0)
				|| (data.page?.totalElements > 0)
			) {
				this.searchResults = true;

				if (this.firstLoad) {
					this.dataSource.paginator = this.paginator;
					this.paginator.page.subscribe((paginator) =>
						this.updatePaginator(paginator)
					);
					this.sort.sortChange.subscribe((sort) => {
						this.paginator.pageIndex = 0;
						this.updatePaginator(this.paginator);
					})
					this.firstLoad = false;
				}

				this.dataSource.connect().next(data.content);
			} else {
				this.dataSource.data = [];
				this.searchResults = false;
				this.firstLoad = true;
			}
			this.totalRows = data.totalElements ?? data.page?.totalElements;
			this.loadingRecords = false;
		});
	}

	reload() {
		this.paginator.pageIndex = 0;
		this.getPagedData(this.paginator.pageIndex, this.paginator.pageSize);
	}

	updatePaginator(paginator: any) {
		this.getPagedData(paginator.pageIndex, paginator.pageSize);
	}

	public sortData(): void {
		if (this.sort.direction === 'desc') {
			this.sort.direction = 'desc';
		} else {
			this.sort.direction = 'asc';
		}
	}

	/** Whether the button toggle group contains the id as an active value. */
	isSticky(id: string) {
		return (this.stickyColumns || []).indexOf(id) !== -1;
	}

	/** Whether the button toggle group contains the id as an active value. */
	isStickyEnd(id: string) {
		return (this.stickyEndColumns || []).indexOf(id) !== -1;
	}

	buildGuardParamRole(column: CustomTableColumn) {
		let customRoles: string[] = [];
		if (column.roles?.length) {
			customRoles = column.roles;
		}
		return {
			customRole: customRoles,
			path: '',
		} as RoleBasedParamModel;
	}

	buildGuardParamBasedRole(): RoleBasedParamModel {
		// Add custom AD role
		let customRoles: string[] = [];
		return {
			customRole: customRoles,
			path: '',
		} as RoleBasedParamModel;
	}

	/** Used on the HTTP component to concatenate the Code and Value for a property displayed on the table */
	concatCodeValueColumn(code: string, value: any) {
		if (code && value) {
			return code + ' - ' + value;
		} else {
			return code ? code : '' + value ? value : '';
		}
	}

	/** Whether the number of selected elements matches the total number of rows. */
	isAllSelected() {
		const numSelected = this.selection.selected.length;
		const numRows = this.dataSource.connect().value.length;
		return numSelected === numRows;
	}

	/** Selects all rows if they are not all selected; otherwise clear selection. */
	toggleAllRows() {
		if (this.isAllSelected()) {
			this.selection.clear();
			return;
		}

		this.selection.select(...this.dataSource.connect().value);
	}

	/** The label for the checkbox on the passed row */
	checkboxLabel(row?: any): string {
		if (!row) {
			return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
		}
		return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.position + 1
			}`;
	}

	onClick(row: any) {
		this.clickedRow.emit(row);
	}

	doButtonClick(buttonAction: Function, row: any) {
		buttonAction(row, this.instance);
	}

	onExport(totalRows: number) {
		this.exportData.emit({ pageable: new Pageable(0, totalRows, this.getPageableSort()) }).then((response: any) => {
			const data: RestPagedResponse<any, any> = response;
			let content = data.content;
			this.columns.filter(column => column.processFunction).forEach(column => {
				content.forEach(record => record[column.valueProperty] = column.processFunction(record[column.valueProperty], this.instance));
			});
			this.columns.filter(column => column.type === 'date').forEach(column => {
				content.forEach(record => record[column.valueProperty] = this.datePipe.transform(record[column.valueProperty],
					column.dateType ? column.dateType : 'MM/dd/yyyy HH:mm:ss.SSS'));
			});
			content = content.map(row => {
				const newRow: any = {};
				this.columns.filter(column => column.exportable).forEach(column => {
					newRow[column.name] = row[column.valueProperty]
				});
				return newRow;
			});
			this.excelService.exportExcel(
				content,
				this.excelName,
				this.excelName,
				false
			);
		});
	}
}
